import { useAuth } from "src/hooks/use-auth";
import {
    PaymentElement,
    useElements,
    useStripe
} from "@stripe/react-stripe-js";
import React, { useState } from "react";
import { profileApi } from "src/api/profile";
import { trackMetaEvent } from "src/libs/analytics/meta";
import { increment } from "firebase/firestore";
import { Box, Button, DialogActions, Typography } from "@mui/material";
import { ErrorOutline } from "@mui/icons-material";
import { tr } from 'src/locales/tr';

const DonateForm = ({ amount, onClose, onSuccess }) => {
    const { user } = useAuth();
    const stripe = useStripe();
    const elements = useElements();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!stripe || !elements) return;

        setLoading(true);
        setError(null);

        try {
            // Подтверждаем платеж с PaymentElement
            const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
                elements,
                confirmParams: {
                    return_url: `${window.location.origin}/donation-success`,
                    payment_method_data: {
                        billing_details: {
                            name: user?.businessName || 'Anonymous',
                        }
                    }
                },
                redirect: 'if_required'
            });

            if (stripeError) throw stripeError;

            // Обработка успешного платежа
            if (paymentIntent?.status === 'succeeded') {
                await profileApi.update(user.id, {
                    totalDonations: increment(amount),
                    lastDonation: new Date(),
                    isSupporter: true
                });

                trackMetaEvent('Donate', { value: amount, currency: 'USD' });
                setSuccess(true);
                onSuccess();
            }
        } catch (err) {
            setError(err.message || tr("Payment failed"));
            console.error('Payment error:', err);
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <Box textAlign="center">
                <Typography variant="h5" color="success.main" gutterBottom>
                    {tr("Thank you for your support!")}
                </Typography>
                <Typography>
                    {tr("Your donation of ${{0}} has been processed successfully.", { 0: amount.toFixed(2) })}
                </Typography>
                <DialogActions sx={{ justifyContent: 'center', mt: 2 }}>
                    <Button onClick={onClose} variant="contained">{tr("Close")}</Button>
                </DialogActions>
            </Box>
        );
    }

    return (
        <form onSubmit={handleSubmit}>
            <PaymentElement
                options={{
                    layout: {
                        type: 'tabs',
                        defaultCollapsed: false,
                    },
                    wallets: {
                        applePay: 'auto',
                        googlePay: 'auto'
                    }
                }}
            />

            {error && (
                <Box
                    sx={{
                        mt: 2,
                        p: 1.5,
                        backgroundColor: 'error.light',
                        color: 'error.main',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <ErrorOutline fontSize="small" />
                    <Typography variant="body2">{error}</Typography>
                </Box>
            )}

            <DialogActions sx={{ mt: 3 }}>
                <Button onClick={onClose} disabled={loading}>
                    {tr("Cancel")}
                </Button>
                <Button
                    type="submit"
                    disabled={!stripe || loading}
                    variant="contained"
                    color="primary"
                    sx={{ minWidth: 120 }}
                >
                    {loading ? tr("Processing...") : tr("Donate ${{0}}", { 0: amount.toFixed(2) })}
                </Button>
            </DialogActions>
        </form>
    );
};

export default DonateForm;